import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
  OnChanges, AfterViewChecked
} from '@angular/core';
import {FormBuilder, FormControl, Validators, AbstractControl, FormGroup, FormArray} from '@angular/forms';
import {BreakdownService} from '../breakdown.service';
import {Breakdown} from '../../../model/quote/breakdown/breakdown.model';
import {QuoteService} from '../../quote.service';
import {ActivatedRoute} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatRadioChange} from '@angular/material/radio';
import {LocalStorageService} from 'ngx-webstorage';
import {environment} from '../../../../environments/environment';
import {formatDate} from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-breakdown-proposer-details',
  templateUrl: './breakdown-proposer-details.component.html',
  styleUrls: ['./breakdown-proposer-details.component.css', '../../quote.component.css']
})
export class BreakdownProposerDetailsComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {

  addresses;
  formFields: any;
  private postcodeLookupSubject = new Subject<string>();
  private postcodeLookupSubscription: Subscription;
  submitted = false;
  isBroker = false;
  brokers: any = [];
  showContactDetails = false;
  UK_HB_INSURANCE_COMPANY_ID = '';
  quote_type: number = this.route.snapshot.params.quote_type ? Number(this.route.snapshot.params.quote_type) : 1;
  ratingType: any;

  // Dates
  minEffectiveDate = new Date();
  minInceptionDate = new Date();
  maxInceptionDate = new Date();
  maxDob: any = new Date();

  // Input Decorators
  @Input() staticData;
  @Input() authDetails;
  @Input() apiModelObject;

  modelObject = new Breakdown();

  // Output Decorators
  @Output() modelObjectUpdated = new EventEmitter<Breakdown>();
  policyEndDateMax: Date = new Date();

  constructor(
    private formBuilder: FormBuilder,
    private breakdownService: BreakdownService,
    private quoteService: QuoteService,
    private route: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private sessionState: LocalStorageService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.getBrokers();

    this.formFields = this.formBuilder.group({
      inception_date: ['', Validators.required],
      cover_to: ['', Validators.required],
      company_id: ['', Validators.required],
      effective_date: ['', Validators.required],
      proposer: this.formBuilder.group({
        proposer_type: ['', Validators.required],
        address: this.proposerAddress(),
        title: ['', Validators.required],
        first_name: ['', Validators.required],
        last_name: [''],
        dob: ['', Validators.required],
        email: ['', Validators.email],
        phone: [''],
        is_email: [0],
        is_phone: [0],
        sms: [0],
        post: [0],
        is_micro_enterprise: [{ value: '', disabled: true }],
        company_name: [{ value: '', disabled: true }],
      }),
    });
    
    // calculate max inception
    const maxDate = new Date().setDate(new Date().getDate() + 30);
    this.maxInceptionDate = new Date(maxDate);
    this.minEffectiveDate = this.startOfToday();

    const currentDate = new Date();
    currentDate.setFullYear(new Date().getFullYear() - 18);
    this.maxDob = currentDate.toISOString().slice(0, 10);

    if (this.quote_type && this.quote_type != 3) {
      this.formFields.get('effective_date').disable();
    } else {
      this.formFields.get('effective_date').enable();
    }

    if (this.quote_type && this.quote_type == 3) {
      this.formFields.get('inception_date').disable();
      this.formFields.get('cover_to').disable();
    } else {
      this.formFields.get('inception_date').enable();
      this.formFields.get('cover_to').enable();
    }

    if (this.sessionState.retrieve('is_broker')) {
      this.isBroker = true;
    }
    this.applyBrokerCompanyId();
    this.UK_HB_INSURANCE_COMPANY_ID = environment.UK_HB_INSURANCE_COMPANY_ID;

    //auto capitalize the first letter of the firstname and surname
    this.proposerControls['first_name'].valueChanges.subscribe((value) => {
      this.proposerControls['first_name'].setValue(value.charAt(0).toUpperCase() + value.slice(1),  { emitEvent: false });
    });

    this.proposerControls['last_name'].valueChanges.subscribe((value) => {
      this.proposerControls['last_name'].setValue(value.charAt(0).toUpperCase() + value.slice(1), { emitEvent: false });
    });

    this.postcodeLookupSubscription = this.postcodeLookupSubject.pipe(debounceTime(400)).subscribe((postcode) => {
      this.doPostcodeLookup(postcode);
    });
  }

  ngOnDestroy() {
    if (this.postcodeLookupSubscription) {
      this.postcodeLookupSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.apiModelObject) {
      this.modelObject = this.apiModelObject;

      this.patchAddressValues();

      if (this.apiModelObject.type == 3) {
        this.quote_type = 3;
      }
      this.minEffectiveDate = this.startOfToday();

      if (this.apiModelObject.is_send_email === 1) {
        this.showContactDetails = true;
      }
      this.patchValues();
    }
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  get proposerControls() {
    return ((this.formFields.get('proposer') as FormGroup).controls);
  }

  get proposerAddressControls() {
    return ((this.proposerControls.address as FormGroup).controls);
  }

  get f(): { [key: string]: AbstractControl } {
    if (this.formFields) {
      return this.formFields.controls;
    }
  }

  public patchAddressValues() {
    this.proposerAddressControls.address1.setValue(this.modelObject.policy_holder_detail.address.address1);
    this.proposerAddressControls.address2.setValue(this.modelObject.policy_holder_detail.address.address2);
    this.proposerAddressControls.address3.setValue(this.modelObject.policy_holder_detail.address.address3);
    this.proposerAddressControls.county.setValue(this.modelObject.policy_holder_detail.address.county ?? this.modelObject.policy_holder_detail.address.city);
    this.proposerAddressControls.postcode.setValue(this.modelObject.policy_holder_detail.address.postcode);
  }

  private applyBrokerCompanyId(): void {
    if (!this.formFields) {
      return;
    }
    if (this.isBroker || this.sessionState.retrieve('company_role') === 'Broker') {
      const brokerCompanyId = this.sessionState.retrieve('broker_company_id');
      if (brokerCompanyId) {
        this.formFields.get('company_id')?.setValue(brokerCompanyId);
      }
    }
  }

  public disablePropserFields(proposer_type: string | number) {
    const isIndividual = proposer_type === 'individual' || proposer_type === 1 || proposer_type === '1';
    const isCompany = proposer_type === 'company' || proposer_type === 2 || proposer_type === '2';
    const isJoint = proposer_type === 'joint' || proposer_type === 3 || proposer_type === '3';
    if (isIndividual) {
      this.proposerControls.title.enable();
      this.proposerControls.first_name.enable();
      this.proposerControls.last_name.enable();
      this.proposerControls.dob.enable();

      this.proposerControls.is_micro_enterprise.clearValidators();
      this.proposerControls.company_name.clearValidators();
      this.proposerControls.is_micro_enterprise.reset({ value: '', disabled: true });
      this.proposerControls.company_name.reset({ value: '', disabled: true });
      (this.proposerControls as any)['year']?.clearValidators();
      (this.proposerControls as any)['year']?.reset({ value: '', disabled: true });
      this.proposerControls.is_micro_enterprise.updateValueAndValidity();
      this.proposerControls.company_name.updateValueAndValidity();
      (this.proposerControls as any)['year']?.updateValueAndValidity();

    } else {
      this.proposerControls.title.disable();
      this.proposerControls.first_name.disable();
      this.proposerControls.last_name.disable();
      this.proposerControls.dob.disable();

      this.proposerControls.company_name.enable();
      this.proposerControls.company_name.setValidators(Validators.required);
      (this.proposerControls as any)['year']?.enable();

      if (isCompany) {
        this.proposerControls.is_micro_enterprise.enable();
        this.proposerControls.is_micro_enterprise.setValidators(Validators.required);
      } else if (isJoint) {
        this.proposerControls.is_micro_enterprise.clearValidators();
        this.proposerControls.is_micro_enterprise.reset({ value: '', disabled: true });
        (this.proposerControls as any)['year']?.disable();
      }

      this.proposerControls.is_micro_enterprise.updateValueAndValidity();
      this.proposerControls.company_name.updateValueAndValidity();
      (this.proposerControls as any)['year']?.updateValueAndValidity();
    }
  }

  public getBrokers() {
    this.quoteService.getBrokers(6) // 'product_type' should be 7, but isn't implemented in the back-end API.
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.brokers = response.result;
            // When editing, re-apply company_id so mat-select option value type matches (avoids blank when returning to page)
            if (this.modelObject?.company_id != null && this.formFields) {
              const broker = this.brokers.find((b: any) => b.id == this.modelObject.company_id || String(b.id) === String(this.modelObject.company_id));
              if (broker) {
                this.formFields.get('company_id').setValue(broker.id);
                if (broker.is_send_email === 1) {
                  this.proposerControls.email?.clearValidators();
                  this.proposerControls.phone?.clearValidators();
                  this.proposerControls.email?.updateValueAndValidity();
                  this.proposerControls.phone?.updateValueAndValidity();
                }
                this.modelObject.brokerCommission = broker.broker_commission;
                this.modelObject.brokerAdminFee = broker.broker_admin_fee;
              }
            }
          }
        },
        (error) => {}
      );
  }

  /**
   * Get broker commission fee and reset form
   * @param event
   */
  public getBrokerCommission(event) {
    const brokerDetails = this.brokers.filter((x => x.id == event.value));
    if (brokerDetails.length > 0) {
      if (brokerDetails[0].is_send_email == 1) {
        this.proposerControls.email?.clearValidators();
        this.proposerControls.phone?.clearValidators();
        this.proposerControls.email?.setValue('');
        this.proposerControls.phone?.setValue('');
        this.proposerControls.is_phone?.setValue(0);
        this.proposerControls.is_email?.setValue(0);
        this.proposerControls.sms?.setValue(0);
        this.proposerControls.post?.setValue(0);

        this.proposerControls.email?.updateValueAndValidity();

        this.proposerControls.phone?.updateValueAndValidity();
      }

      // set comission fees
      this.modelObject.brokerCommission = brokerDetails[0].broker_commission;
      this.modelObject.brokerAdminFee = brokerDetails[0].broker_admin_fee;
    }
    this.showContactDetails = true;
  }

  public onPolicyStartDateChange(event) {
    this.setCoverToDate(event.value);
  }

  public onProposerChange(event: MatRadioChange) {
    const raw = event?.value;
    const numeric = this.normalizeProposerTypeToNumber(raw);
    if (numeric != null) {
      this.proposerControls.proposer_type.setValue(numeric, { emitEvent: false });
      if (this.modelObject?.policy_holder_detail) {
        this.modelObject.policy_holder_detail.proposer_type = numeric;
      }
    }
    this.disablePropserFields(numeric ?? raw);
  }

  private normalizeProposerTypeToNumber(value: string | number): number | null {
    if ([1,2,3].includes(value as number)) return value as number;
    if (value === 'individual') return 1;
    if (value === 'company') return 2;
    if (value === 'joint') return 3;
    return null;
  }

  private parseIsMicroEnterpriseFromApi(raw: any): boolean | '' {
    if (raw === true || raw === 1 || raw === '1') return true;
    if (raw === false || raw === 0 || raw === '0') return false;
    return '';
  }

  private coerceIsMicroEnterpriseForApi(value: any): number | null {
    if (value === '' || value === null || value === undefined) return null;
    if (value === true || value === 1 || value === '1') return 1;
    if (value === false || value === 0 || value === '0') return 0;
    return null;
  }


  public onSubmit() {
    this.submitted = true;

    const proposerType = this.proposerControls.proposer_type.value;
    if (proposerType !== '' && proposerType != null) {
      this.disablePropserFields(proposerType);
    }
    this.applyBrokerCompanyId();

    if (this.quote_type !== 3 && !this.modelObject.effectiveDate) {
      this.formFields.get('effective_date')!.disable();
    }

    // if processing an mta
    if (this.quote_type === 3) {
      const eff = this.formFields.get('effective_date')?.value;
      // if effective date is before today, set error and return false
      if (eff && this.isBeforeToday(eff)) {
        this.formFields.get('effective_date')?.setErrors({ matDatepickerMin: true });
        this.formFields.get('effective_date')?.markAsTouched();
        this._snackBar.open('MTA effective date cannot be before today', 'Close', { duration: 3000 });
        return false;
      }
    }

    if (this.formFields.invalid) {
      this.scrollToFirstInvalidControl();
      return false;
    }

    const formData = {
      ...this.formFields.value,
      proposer: (this.formFields.get('proposer') as FormGroup).getRawValue(),
    };
    this.applyFormDataToModel(formData);
    this.applyProposerDatesToModel(formData);
    this.applyMtaQuoteTypeIfNeeded(formData);
    this.sanitizeDrivers();
    this.sanitizeVehicles();

    const data = this.buildQuoteSavePayload();
    this.saveQuote(data);
  }

  private scrollToFirstInvalidControl(): void {
    const firstInvalid = document.querySelector('.ng-invalid');
    if (firstInvalid) {
      (firstInvalid as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      (firstInvalid as HTMLElement).focus();
    }
  }

  private applyFormDataToModel(formData: any): void {
    this.modelObject.cover_to = formData.cover_to ? formatDate(formData.cover_to, 'yyyy-MM-dd', 'en-US') : this.modelObject.cover_to;
    this.modelObject.broker_id = this.sessionState.retrieve('userInfo').id;
    this.modelObject.policy_holder_detail = formData.proposer;
    const pt = this.normalizeProposerTypeToNumber(this.modelObject.policy_holder_detail.proposer_type);
    if (pt != null) {
      this.modelObject.policy_holder_detail.proposer_type = pt;
    }
    // this.modelObject.ratingType = formData.rating_type;

    // if (this.sessionState.retrieve('company_role') == 'Broker') {
      // this.modelObject.company_id = this.sessionState.retrieve('broker_company_id');
    // } else {
    this.modelObject.company_id = formData.company_id;
    // }
    if (this.route.snapshot.params.id) {

    } else {
      if (!this.modelObject.id) {
        this.modelObject.vehicles = null;
      }
    }

    const inceptionDate = formData.inception_date ?? this.formFields.get('inception_date').value;

    this.modelObject.inception_date = formatDate(inceptionDate, 'yyyy-MM-dd', 'en-US');
    if (formData.proposer && formData.proposer.dob) {
      if (formData.proposer.dob) {
        this.modelObject.policy_holder_detail.dob = formatDate(formData.proposer.dob, 'yyyy-MM-dd', 'en-US');
      }
      if (formData.proposer.licence_length) {
        this.modelObject.policy_holder_detail.licence_length = formatDate(formData.proposer.licence_length, 'yyyy-MM-dd', 'en-US');
      }
    }
    this.modelObject.declaration = true;

    this.modelObject.company_id = this.sessionState.retrieve('company_role') === 'Broker'
      ? this.sessionState.retrieve('broker_company_id')
      : formData.company_id;

    if (!this.route.snapshot.params.id && !this.modelObject.id) {
      this.modelObject.vehicles = null;
    }
  }

  private applyProposerDatesToModel(formData: any): void {
    const proposer = formData.proposer;
    if (!proposer) return;
    this.modelObject.policy_holder_detail.dob = this.formatDateSafe(proposer.dob);
    this.modelObject.policy_holder_detail.licence_length = this.formatDateSafe(proposer.licence_length);
  }

  /** Safe date format for API; returns '' if value is null/undefined to avoid "Unable to convert into a date" errors. */
  private formatDateSafe(value: any, dateFormat: string = 'yyyy-MM-dd'): string {
    return value ? formatDate(value, dateFormat, 'en-US') : '';
  }

  private startOfToday(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  private isBeforeToday(date: Date | string): boolean {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() < this.startOfToday().getTime();
  }

  private applyMtaQuoteTypeIfNeeded(formData: any): void {
    if (this.quote_type !== 3 && this.quote_type !== 4) return;

    this.modelObject.type = this.quote_type;
    if (this.quote_type === 3) {
      this.modelObject.effectiveDate = this.formatDateSafe(formData.effective_date);
    }

    // On the first MTA/Cancellation save the loaded record is the original policy —
    // null the id so the backend creates a new record instead of overwriting the policy.
    if (Number(this.apiModelObject?.is_policy) === 1) {
      const policyId = this.apiModelObject.id
        || (this.modelObject as any).old_policy_id
        || (this.modelObject as any).oldPolicyId;
      if (policyId) {
        this.modelObject.oldPolicyId = policyId;
        (this.modelObject as any).old_policy_id = policyId;
      }
      this.modelObject.id = null;
    }
  }

  private sanitizeDrivers(): void {
    if (this.modelObject.drivers) {
      this.modelObject.drivers = this.modelObject.drivers.filter((d: any) => d.first_name != null && d.first_name !== '');
    }
    if (this.modelObject.type === 2) {
      this.modelObject.policyId = null;
      this.modelObject.effectiveDate = null;
    }
    if (this.modelObject.drivers?.length === 1 && !this.modelObject.drivers[0].id) {
      this.modelObject.drivers = [];
    }
  }

  private sanitizeVehicles(): void {
    if (!this.modelObject.vehicles?.length) return;
    this.modelObject.vehicles.forEach((v: any) => {
      v.owner = v.owner || '';
      v.owner_name = '';
      v.registration_number = v.registration_number || '';
      v.make = v.make || '';
      v.model = v.model || '';
      v.year = v.year || '';
      v.weight = v.weight || '';
      v.ownership = v.mileage || '';
      v.mileage = v.mileage || '';
      v.ncd = v.mileage || '';
      v.postcode = v.postcode || '';
      v.tracker = v.tracker || '';
    });
  }

  private buildQuoteSavePayload(): any {
    const isMtaOrCancel = this.quote_type === 3 || this.quote_type === 4;
    const isFirstSave = isMtaOrCancel && Number(this.apiModelObject?.is_policy) === 1;
    const oldPolicyId = this.modelObject.oldPolicyId || (this.modelObject as any).old_policy_id || (isFirstSave ? this.apiModelObject?.id : null);
    const ph = this.modelObject.policy_holder_detail;
    const isMicroPayload =
      Number(ph.proposer_type) === 2
        ? this.coerceIsMicroEnterpriseForApi(ph.is_micro_enterprise)
        : null;

    const payload = {
      "quote_data": {
        "id": this.modelObject.id,
        "company_id": this.modelObject.company_id,
        "policy_start_date": this.modelObject.inception_date,
        "policy_end_date": this.modelObject.cover_to,
        "broker_id": this.modelObject.broker_id,
        "type": this.quote_type,
        "policy_ref_number": this.modelObject.policyRefNumber || (this.modelObject as any).policy_ref_number,
        "old_policy_id": oldPolicyId,
        "sequence_number": this.modelObject.sequenceNumber || (this.modelObject as any).sequence_number,
        "quote_meta_id": this.modelObject.quote_meta_id,
        ...(this.quote_type === 3 ? { "effective_date": this.modelObject.effectiveDate || (this.modelObject as any).effective_date } : {}),
        "policy_holder": {
          "title": this.modelObject.policy_holder_detail.title,
          "first_name": this.modelObject.policy_holder_detail.first_name,
          "last_name": this.modelObject.policy_holder_detail.last_name,
          "email": this.modelObject.policy_holder_detail.email,
          "phone": this.modelObject.policy_holder_detail.phone,
          "mobile": this.modelObject.policy_holder_detail.mobile ? this.modelObject.policy_holder_detail.mobile : '',
          "dob": this.modelObject.policy_holder_detail.dob,
          "company_name": this.modelObject.policy_holder_detail.company_name ? this.modelObject.policy_holder_detail.company_name : '',
          "proposer_type": this.modelObject.policy_holder_detail.proposer_type,
          "address": {
            "address1": this.modelObject.policy_holder_detail.address.address1,
            "address2": this.modelObject.policy_holder_detail.address.address2,
            "address3": this.modelObject.policy_holder_detail.address.address3,
            "address4": this.modelObject.policy_holder_detail.address.address4,
            "city": this.proposerAddressControls.county.value,
            "country": "UK",
            "postcode": this.proposerAddressControls.postcode.value,
          },
          "sms": this.modelObject.policy_holder_detail.sms,
          "post": this.modelObject.policy_holder_detail.post,
          "is_phone": this.modelObject.policy_holder_detail.is_phone,
          "is_email": this.modelObject.policy_holder_detail.is_email,
          "is_micro_enterprise": isMicroPayload,
        },
        // "vehicle_details": [this.modelObject.vehicles],
      }
    };
    return payload;
  }

  private saveQuote(data: any): void {
    this.breakdownService.saveQuote(data).subscribe(
      (res: any) => {
        if (res.success && res.result?.quote) {
          this.modelObject = res.result.quote;
          this.modelObject.type = this.quote_type;
          this.modelObjectUpdated.emit(this.modelObject);
        } else if (!res.success && res.message) {
          this._snackBar.open(res.message, '', {
            duration: 5000,
            panelClass: 'text-danger'
          });
        }
      }
    );
  }

  patchValues() {
    
    this.minEffectiveDate = this.startOfToday();
    let effectiveDate = this.minEffectiveDate;
    if (this.modelObject.effectiveDate) {
      effectiveDate = new Date(this.modelObject.effectiveDate);
      effectiveDate.setHours(0, 0, 0, 0);
      if (effectiveDate < this.minEffectiveDate) {
        effectiveDate = this.minEffectiveDate;
      }
    }

    // Use matching broker id when available so mat-select option value type matches (avoids blank when editing)
    let companyIdValue = this.modelObject.company_id;
    if (this.modelObject.company_id != null && this.brokers?.length) {
      const broker = this.brokers.find((b: any) => b.id == this.modelObject.company_id || String(b.id) === String(this.modelObject.company_id));
      if (broker) companyIdValue = broker.id;
    }

    this.formFields.patchValue({
      inception_date: new Date(this.modelObject.inception_date),
      company_id: companyIdValue,
      proposer: this.modelObject.policy_holder_detail,
      effective_date: effectiveDate,
    });

    if ((typeof this.modelObject.policy_holder_detail.proposer_type) === 'string') {
      switch (this.modelObject.policy_holder_detail.proposer_type) {
        case 'individual':
          this.proposerControls.proposer_type.setValue(1);
          this.disablePropserFields(1);
          break;
        case 'company':
          this.proposerControls.proposer_type.setValue(2);
          this.disablePropserFields(2);
          break;
        case 'joint':
          this.proposerControls.proposer_type.setValue(3);
          this.disablePropserFields(3);
          break;
      }
    } else if ((typeof this.modelObject.policy_holder_detail.proposer_type) === 'number') {
      switch (this.modelObject.policy_holder_detail.proposer_type) {
        case 1:
          this.proposerControls.proposer_type.setValue(1);
          this.disablePropserFields(1);
          break;
        case 2:
          this.proposerControls.proposer_type.setValue(2);
          this.disablePropserFields(2);
          break;
        case 3:
          this.proposerControls.proposer_type.setValue(3);
          this.disablePropserFields(3);
          break;
      }
    }

    // if (this.sessionState.retrieve('is_broker')) {
    //   this.formFields.get('company_id').setValue(this.sessionState.retrieve('broker_company_id'));
    // }

    this.setCoverToDate(this.modelObject.inception_date);

    const ph = this.modelObject.policy_holder_detail;
    const rawMe = ph?.is_micro_enterprise ?? ph?.micro_enterprise;
    const me = this.parseIsMicroEnterpriseFromApi(rawMe);
    this.proposerControls.is_micro_enterprise.setValue(me, { emitEvent: false });
    if (ph) {
      ph.is_micro_enterprise = me === '' ? null : me ? 1 : 0;
    }

    if (this.apiModelObject.financial_details) {
      this.modelObject.brokerCommission = this.apiModelObject.financial_details.broker_commission;
      this.modelObject.brokerAdminFee = this.apiModelObject.financial_details.broker_admin_fee;
    } else {
      const brokerDetails = this.brokers.filter((x => x.id === this.modelObject.company_id));
      if (brokerDetails.length > 0) {
        this.modelObject.brokerCommission = brokerDetails[0].broker_commission;
        this.modelObject.brokerAdminFee = brokerDetails[0].broker_admin_fee;
      }
    }
  }


  /** Called on keyup; debounced to avoid pinging the API on every keystroke. */
  postcodeLookup(event: any) {
    const postcode = (event?.target?.value || '').trim();
    if (!postcode || postcode.length < 4) {
      this.addresses = [];
      return;
    }
    this.postcodeLookupSubject.next(postcode);
  }

  /** Backend-proxied postcode lookup (address/search). Called after debounce. */
  private doPostcodeLookup(postcode: string) {
    const trimmed = (postcode || '').trim();
    if (!trimmed || trimmed.length < 4) {
      this.addresses = [];
      return;
    }
    this.quoteService.getAddress(trimmed).subscribe({
      next: (response: any) => {
        const records = response?.records || response?.addresses || [];
        this.addresses = Array.isArray(records) ? records : [];
      },
      error: () => {
        this.addresses = [];
      }
    });
  }
  
  /** Called when user selects an address from the list. Supports backend lookup (id + l) and legacy (full object). */
  onSelectAddress(record: any) {
    if (!record) return;
    const currentPostcode = this.proposerAddressControls.postcode.value;
    // Backend/Simply: record has id and l; fetch full address by id
    if (record.id != null && record.id !== '') {
      this.quoteService.getAddressById(record.id).subscribe({
        next: (full: any) => {
          this.applyAddressToForm(full);
          setTimeout(() => {
            if (this.proposerAddressControls.postcode.value !== currentPostcode) {
              this.proposerAddressControls.postcode.setValue(currentPostcode);
            }
          }, 0);
        },
        error: () => {}
      });
      return;
    }
    // Legacy format (e.g. getAddress.io): full address object
    this.applyAddressToForm(record);
    setTimeout(() => {
      if (this.proposerAddressControls.postcode.value !== currentPostcode) {
        this.proposerAddressControls.postcode.setValue(currentPostcode);
      }
    }, 0);
  }

  private applyAddressToForm(address: any) {
    if (!address || !this.proposerAddressControls) return;
    const a = address;
    const line1 = a.line1 ?? '';
    const line2 = a.line2 ?? '';
    const town = a.town ?? '';
    const county = a.county ?? '';
    const postcode = a.postcode ?? a.Postcode ?? '';
    this.proposerAddressControls.address1.setValue(line1 || '');
    this.proposerAddressControls.address2.setValue(line2 || '');
    this.proposerAddressControls.address3.setValue(town || '');
    this.proposerAddressControls.county.setValue(county || '');
    if (postcode) this.proposerAddressControls.postcode.setValue(postcode);
  }

  /**
   * Init address form
   * @returns 
   */
  public proposerAddress() {
    return this.formBuilder.group({
      address1: ['', Validators.required],
      address2: [''],
      address3: [''],
      county: ['', Validators.required],
      postcode: ['', Validators.required]
    });
  }


  public setCoverToDate(value) {
    const inceptionDate = new Date(value);
    const endDate = new Date(value);
    endDate.setFullYear(inceptionDate.getFullYear() + 1);
    endDate.setDate(endDate.getDate() - 1);

    this.policyEndDateMax = endDate;
    this.formFields.get('cover_to').setValue(this.policyEndDateMax);
    this.formFields.get('inception_date').setValue(inceptionDate);
  }
}
